import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import { postQuery,fetchData } from "../../services/BackendServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../pages/Requirement/components/common/AlerModel";

const BankInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rfqId, setRfqId] = useState("");
  const [rfqNo, setRfqNo] = useState("");
  const [passedInstrument, setPassedInstrument] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankId, setBankId] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const initialFormData = {
    BankName: "",
    BankID: "",
    Instrument: "",
    InterestRateMargin: "",
    IssuanceCharges: "",
    ConfirmationCharges: "",
    TenorOffered: "",
    Currency: "USD",
    TotalCost: "",
    ConditionSummary: "",
    OfferValidity: "",
    requiredDocuments: {
      commercialInvoice: false,
      billOfLading: false,
      certificateOfAnalysis: false,
      packingList: false,
      certificateOfOrigin: false,
      insurancePolicy: false,
      inspectionCertificate: false,
    },
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        requiredDocuments: {
          ...prev.requiredDocuments,
          [name]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.BankName) {
      newErrors.BankName = "Bank Name is required";
    } else if (formData.BankName.length > 100) {
      newErrors.BankName = "Bank Name must not exceed 100 characters";
    }

    if (!formData.BankID) {
      newErrors.BankID = "Bank ID is required";
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.BankID)) {
      newErrors.BankID = "Bank ID must be alphanumeric";
    } else if (formData.BankID.length > 50) {
      newErrors.BankID = "Bank ID must not exceed 50 characters";
    }

    if (!formData.Instrument) {
      newErrors.Instrument = "Instrument is required";
    } else if (formData.Instrument.length > 100) {
      newErrors.Instrument = "Instrument must not exceed 100 characters";
    }

    if (!formData.InterestRateMargin) {
      newErrors.InterestRateMargin = "Interest Rate Margin is required";
    } else if (
      isNaN(formData.InterestRateMargin) ||
      Number(formData.InterestRateMargin) <= 0
    ) {
      newErrors.InterestRateMargin =
        "Interest Rate Margin must be a positive number";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.InterestRateMargin)) {
      newErrors.InterestRateMargin =
        "Interest Rate Margin must have up to 2 decimal places";
    }

    if (!formData.IssuanceCharges) {
      newErrors.IssuanceCharges = "Issuance Charges is required";
    } else if (
      isNaN(formData.IssuanceCharges) ||
      Number(formData.IssuanceCharges) <= 0
    ) {
      newErrors.IssuanceCharges = "Issuance Charges must be a positive number";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.IssuanceCharges)) {
      newErrors.IssuanceCharges =
        "Issuance Charges must have up to 2 decimal places";
    }

    if (!formData.ConfirmationCharges) {
      newErrors.ConfirmationCharges = "Confirmation Charges is required";
    } else if (
      isNaN(formData.ConfirmationCharges) ||
      Number(formData.ConfirmationCharges) <= 0
    ) {
      newErrors.ConfirmationCharges =
        "Confirmation Charges must be a positive number";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.ConfirmationCharges)) {
      newErrors.ConfirmationCharges =
        "Confirmation Charges must have up to 2 decimal places";
    }

    if (!formData.TenorOffered) {
      newErrors.TenorOffered = "Tenor Offered is required";
    } else if (formData.TenorOffered.length > 50) {
      newErrors.TenorOffered = "Tenor Offered must not exceed 50 characters";
    }

    if (!formData.Currency) {
      newErrors.Currency = "Currency is required";
    } else if (!/^[A-Z]{3}$/.test(formData.Currency)) {
      newErrors.Currency = "Currency must be a 3-letter code (e.g., USD)";
    }

    if (!formData.TotalCost) {
      newErrors.TotalCost = "Total Cost is required";
    } else if (isNaN(formData.TotalCost) || Number(formData.TotalCost) <= 0) {
      newErrors.TotalCost = "Total Cost must be a positive number";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.TotalCost)) {
      newErrors.TotalCost = "Total Cost must have up to 2 decimal places";
    }

    if (!formData.ConditionSummary) {
      newErrors.ConditionSummary = "Condition Summary is required";
    } else if (formData.ConditionSummary.length > 500) {
      newErrors.ConditionSummary =
        "Condition Summary must not exceed 500 characters";
    }

    if (!formData.OfferValidity) {
      newErrors.OfferValidity = "Offer Validity is required";
    } else if (!dayjs(formData.OfferValidity).isValid()) {
      newErrors.OfferValidity = "Offer Validity must be a valid date";
    } else if (dayjs(formData.OfferValidity).isBefore(dayjs().startOf("day"))) {
      newErrors.OfferValidity = "Offer Validity must be a future date";
    }

    if (!Object.values(formData.requiredDocuments).some((value) => value)) {
      newErrors.requiredDocuments = "At least one document must be selected";
    }

    console.log("newErrors@@", newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDocumentsString = (data) => {
    const labelMap = {
      commercialInvoice: "Commercial Invoice",
      billOfLading: "Bill of Lading",
      certificateOfAnalysis: "Certificate of Analysis",
      packingList: "Packing List",
      certificateOfOrigin: "Certificate of Origin",
      insurancePolicy: "Insurance Policy",
      inspectionCertificate: "Inspection Certificate",
    };
    return Object.entries(data)
      .filter(([_, value]) => value)
      .map(([key]) => labelMap[key])
      .join(", ");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!validateForm()) {
        console.log("Validation failed");
        throw new Error("Form validation failed");
      }
      const existingOffer = await fetchData({
        target: "listFinanceOffers",
        userOrder: "FinanceRfqOfferID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { FinanceRfqID: rfqId, BankID: formData.BankID },
      });
      if (existingOffer.items && existingOffer.items.length > 0){
        throw new Error("Bank Offer already submitted.");
      }

      const financeOffersInput = {
        FinanceRfqOfferID: uuidv4(),
        FinanceRfqID: rfqId,
        BankName: formData.BankName,
        BankID: formData.BankID,
        Instrument: formData.Instrument,
        ConfirmationCharges: parseFloat(formData.ConfirmationCharges) || 0,
        IssuanceCharges: parseFloat(formData.IssuanceCharges) || 0,
        InterestRateMargin: parseFloat(formData.InterestRateMargin) || 0,
        TenorOffered: formData.TenorOffered,
        Currency: formData.Currency,
        TotalCost: parseFloat(formData.TotalCost) || 0,
        ConditionSummary: formData.ConditionSummary,
        OfferValidity: formData.OfferValidity,
        RequiredDocuments: getDocumentsString(formData.requiredDocuments),
        Status: "pending",
      };

      const financeOfferBody = {
        target: "createFinanceOffers",
        financeOffersInput,
      };

      console.log("financeOfferBody@@", financeOfferBody);
      console.log("Calling postQuery with payload:", financeOfferBody);
      const financeResult = await postQuery(financeOfferBody);
      console.log("financeResult:", financeResult);
      if (!financeResult?.items) {
        throw new Error("Failed to create RFQ bank offer");
      }

      const mailMessage = `
        <p>A new bank offer has been submitted. Please find all the submitted information below for your reference.</p>
        <strong><u>Bank Offer Details</u></strong>
        <p>
          <b>Bank Name: </b>${formData.BankName}<br/>
          <b>Bank ID: </b>${formData.BankID}<br/>
          <b>Instrument: </b>${formData.Instrument}<br/>
          <b>Interest Rate Margin (%): </b>${formData.InterestRateMargin}<br/>
          <b>Issuance Charges: </b>${formData.IssuanceCharges}<br/>
          <b>Confirmation Charges: </b>${formData.ConfirmationCharges}<br/>
          <b>Tenor Offered: </b>${formData.TenorOffered}<br/>
          <b>Currency: </b>${formData.Currency}<br/>
          <b>Total Cost: </b>${formData.TotalCost}<br/>
          <b>Condition Summary: </b><p>${formData.ConditionSummary}</p>
          <b>Offer Validity: </b>${
            formData.OfferValidity
              ? dayjs(formData.OfferValidity).format("DD-MM-YYYY")
              : ""
          }<br/>
          <b>Required Documents: </b>${getDocumentsString(
            formData.requiredDocuments
          )}<br/>
        </p>
        <p>Please review the bank offer details.</p>
      `;

      const bodyMail = {
        target: "financerfqmail",
        MailInput: {
          toAddresses: [
            "subhasish.mukherjee@agropreneurs.in",
            "tirtha.roy@agropreneurs.in",
            "amit.mitra@agropreneurs.in",
            buyerEmail,
          ],
          subject: "New Bank Offer Notification",
          msg: mailMessage,
        },
      };

      await postQuery(bodyMail, "backendops");

      setFormData(initialFormData);
      setErrors({});

      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: `Bank offer submitted successfully.`,
        autoClose: false,
      });
    } catch (error) {
      console.error("Submission error:", error, {
        message: error.message,
        name: error.name,
        stack: error.stack,
        errorType: typeof error,
      });
      const errorMessage =
        error.message || "An unexpected error occurred during submission";
      setErrors((prev) => ({
        ...prev,
        form: errorMessage,
      }));
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || `An error occurred during submission`,
        autoClose: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const base64ToUtf8 = (base64) => {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Base64 decoding error:", error);
      console.error("Input base64:", base64);
      return null;
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encoded = searchParams.get("p");

    if (encoded) {
      try {
        const decoded = base64ToUtf8(decodeURIComponent(encoded));
        const params = new URLSearchParams(decoded);

        const id = decodeURIComponent(params.get("id") || "");
        const rfqNo = decodeURIComponent(params.get("RfqId") || "");
        const InstrumentName = decodeURIComponent(params.get("instrument") || "");
        const bankName = decodeURIComponent(params.get("bankName") || "");
        const bankId = decodeURIComponent(params.get("bankId") || "");
        const user = decodeURIComponent(params.get("user") || "");

        setRfqId(id);
        setRfqNo(rfqNo);
        setPassedInstrument(InstrumentName);
        setBankName(bankName);
        setBankId(bankId);
        setBuyerEmail(user);

        setFormData((prev) => ({
          ...prev,
          Instrument: InstrumentName,
          BankName: bankName,
          BankID: bankId,
        }));

        console.log("Decoded Parameters:", {
          rfqId: id,
          rfqNo: rfqNo,
          InstrumentName: InstrumentName,
          bankName,
          bankId,
          user,
        });
      } catch (error) {
        console.error("Error decoding query parameters:", error);
      }
    } else {
      console.error("No encrypted parameter found in URL");
    }
  }, [location.search]);

  return (
    <div className="container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxWidth: 800,
            p: 3,
            boxShadow: "1px 1px 5px #ccc",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#de6b14",
              pb: 1,
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#de6b14", fontWeight: "bold" }}
            >
              Bank Offer : {rfqNo}
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <div className="row g-3">
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Bank Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="BankName"
                  value={formData.BankName}
                  onChange={handleChange}
                  placeholder="Enter Bank Name"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.BankName}
                  helperText={errors.BankName}
                  InputProps={{ readOnly: true }}
                />
              </div>
              <div className="col-md-6" style={{ display: "none" }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Bank ID <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="BankID"
                  value={formData.BankID}
                  onChange={handleChange}
                  placeholder="Enter Bank ID"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.BankID}
                  helperText={errors.BankID}
                  InputProps={{ readOnly: true }}
                  type="hidden"
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Instrument <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="Instrument"
                  value={formData.Instrument}
                  onChange={handleChange}
                  placeholder="Enter Instrument"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.Instrument}
                  helperText={errors.Instrument}
                  InputProps={{ readOnly: true }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Interest Rate Margin <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  name="InterestRateMargin"
                  value={formData.InterestRateMargin}
                  onChange={handleChange}
                  placeholder="Enter Interest Rate Margin"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.InterestRateMargin}
                  helperText={errors.InterestRateMargin}
                  inputProps={{ step: "any" }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Issuance Charges <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  name="IssuanceCharges"
                  value={formData.IssuanceCharges}
                  onChange={handleChange}
                  placeholder="Enter Issuance Charges"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.IssuanceCharges}
                  helperText={errors.IssuanceCharges}
                  inputProps={{ step: "any" }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Confirmation Charges <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  name="ConfirmationCharges"
                  value={formData.ConfirmationCharges}
                  onChange={handleChange}
                  placeholder="Enter Confirmation Charges"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.ConfirmationCharges}
                  helperText={errors.ConfirmationCharges}
                  inputProps={{ step: "any" }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Tenor Offered <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="TenorOffered"
                  value={formData.TenorOffered}
                  onChange={handleChange}
                  placeholder="Enter Tenor Offered"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.TenorOffered}
                  helperText={errors.TenorOffered}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Currency <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="Currency"
                  value={formData.Currency}
                  onChange={handleChange}
                  placeholder="Enter Currency"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.Currency}
                  helperText={errors.Currency}
                  InputProps={{ readOnly: true }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Total Cost <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  name="TotalCost"
                  value={formData.TotalCost}
                  onChange={handleChange}
                  placeholder="Enter Total Cost"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.TotalCost}
                  helperText={errors.TotalCost}
                  inputProps={{ step: "any" }}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Condition Summary <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="ConditionSummary"
                  value={formData.ConditionSummary}
                  onChange={handleChange}
                  placeholder="Enter Condition Summary"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.ConditionSummary}
                  helperText={errors.ConditionSummary}
                />
              </div>
              <div className="col-md-6">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Offer Validity <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  name="OfferValidity"
                  value={formData.OfferValidity}
                  onChange={handleChange}
                  placeholder="Enter Offer Validity"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: "white" }}
                  error={!!errors.OfferValidity}
                  helperText={errors.OfferValidity}
                  inputProps={{
                    min: dayjs().format("YYYY-MM-DD"),
                  }}
                />
              </div>
              <div className="col-md-6">
                <FormControl fullWidth>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Required Documents <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <div className="d-flex flex-wrap gap-3">
                    {[
                      {
                        label: "Commercial Invoice",
                        name: "commercialInvoice",
                      },
                      { label: "Bill of Lading (BL)", name: "billOfLading" },
                      {
                        label: "Certificate of Analysis (COA)",
                        name: "certificateOfAnalysis",
                      },
                      { label: "Packing List", name: "packingList" },
                      {
                        label: "Certificate of Origin",
                        name: "certificateOfOrigin",
                      },
                      { label: "Insurance Policy", name: "insurancePolicy" },
                      {
                        label: "Inspection Certificate",
                        name: "inspectionCertificate",
                      },
                    ].map((doc) => (
                      <FormControlLabel
                        key={doc.name}
                        control={
                          <Checkbox
                            name={doc.name}
                            checked={formData.requiredDocuments[doc.name]}
                            onChange={handleChange}
                            size="small"
                            sx={{
                              color: "#de6b14",
                              "&.Mui-checked": {
                                color: "#de6b14",
                              },
                            }}
                          />
                        }
                        label={doc.label}
                      />
                    ))}
                  </div>
                  {errors.requiredDocuments && (
                    <Typography variant="caption" color="error">
                      {errors.requiredDocuments}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="col-md-12">
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: "#ff9800",
                      "&:hover": { bgcolor: "#fb8c00" },
                      textTransform: "none",
                      px: 2,
                      py: 0.5,
                      width: "auto",
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </div>
              {errors.form && (
                <Typography variant="caption" color="error" sx={{ mt: 2 }}>
                  {errors.form}
                </Typography>
              )}
            </div>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default BankInfo;
